import { type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

export type TitleId = 'ms' | 'mr' | 'unspecified';

export const titleLabels: Record<TitleId, MessageDescriptor> = {
  ms: msg({
    id: 'components.client-form.title.ms.label',
    message: 'Ms.',
  }),
  mr: msg({
    id: 'components.client-form.title.mr.label',
    message: 'Mr.',
  }),
  unspecified: msg({
    id: 'components.client-form.title.unspecified.label',
    message: 'Unspecified',
  }),
};

/**
 *
 * Compute a full name from title, first name and last name:
 * Ex: "Mme" + "Jane" + "Dupont" will give "Mme Jane Dupont"
 * Ex: "Mme" + no first name + "Dupont" will give "Mme Dupont"
 * Ex: "Mr" + no first name and no last name will return undefined
 * Ex: "Unspecified" + "Charlie" + "Doe" will give "Charlie Doe"
 *
 */
export const useComputeFullName = () => {
  const { i18n } = useLingui();

  return {
    computeFullName: ({
      titleId,
      firstName,
      lastName,
    }: {
      titleId: TitleId | null;
      firstName?: string;
      lastName?: string;
    }) => {
      if (!firstName && !lastName) {
        return '';
      }

      let clientComposedName = '';

      if (titleId && titleId !== 'unspecified') {
        clientComposedName += i18n._(titleLabels[titleId]);
      }

      if (firstName) {
        clientComposedName += ` ${firstName}`;
      }

      if (lastName) {
        clientComposedName += ` ${lastName}`;
      }

      return clientComposedName.trim();
    },
  };
};
